/* eslint-env browser */
import React, { FunctionComponent, useCallback, useContext, useEffect, useRef } from 'react';

export enum KeyboardLayerLevel {
  App = 0,
  Screen = 1,
  Feature = 2,
  Modal = 3,
  Focus = 4,
}

type KeyboardShortcut = (event: KeyboardEvent) => void;

interface ModifierKeys {
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
}

interface ShortcutConfig {
  callback: KeyboardShortcut;
  modifiers?: ModifierKeys;
}

type ShortcutValue = KeyboardShortcut | ShortcutConfig;

interface KeyboardShortcuts {
  [key: string]: Set<ShortcutValue>;
}

interface KeyboardLayerShortcuts {
  [key: string]: ShortcutValue;
}

interface KeyboardLayer {
  name: string;
  level: KeyboardLayerLevel;
  appends?: KeyboardLayerShortcuts;
  overrides?: KeyboardLayerShortcuts;
  suppressBelow?: boolean;
  mergeDuplicates?: boolean;
}

interface KeyboardShortcutProviderProps {
  initialShortcuts?: KeyboardLayerShortcuts;
}

interface KeyboardLayerContextState {
  addLayer?: (shortcuts: KeyboardLayer) => void;
  removeLayer?: (name: string) => void;
}

interface KeyboardLayersRef {
  layers: KeyboardLayer[];
  currentShortcuts: KeyboardShortcuts;
}

const KeyboardLayerContext = React.createContext<KeyboardLayerContextState>({
  addLayer: null,
  removeLayer: null,
});

const calculateCurrentShortcuts = (shortcutLayers: KeyboardLayer[]): KeyboardShortcuts =>
  shortcutLayers.reduce((shortcuts, layer) => {
    const appends = layer.appends || {};
    const overrides = layer.overrides || {};

    if (layer.suppressBelow) {
      shortcuts = {};
    }

    Object.entries(appends).forEach(([key, shortcutConfig]) => {
      if (shortcuts[key]) {
        if (!layer.mergeDuplicates) {
          console.error(`useKeyboardShortcuts: append key collision on "${key}"`);
          return;
        }
        shortcuts[key].add(shortcutConfig);
      } else {
        shortcuts[key] = new Set([shortcutConfig]);
      }
    });

    Object.entries(overrides).forEach(([key, shortcutConfig]) => {
      if (shortcuts[key] && layer.mergeDuplicates) {
        shortcuts[key].add(shortcutConfig);
      } else {
        shortcuts[key] = new Set([shortcutConfig]);
      }
    });

    return shortcuts;
  }, {});

const sortLayers = (shortcutLayers: KeyboardLayer[]) =>
  shortcutLayers.sort((a, b) => a.level - b.level);

export const KeyboardShortcutProvider: FunctionComponent<
  React.PropsWithChildren<KeyboardShortcutProviderProps>
> = ({ children, initialShortcuts = {} }) => {
  const initialLayer = {
    name: 'KeyboardShortcutProvider.initialShortcuts',
    level: KeyboardLayerLevel.App,
    appends: initialShortcuts,
  };
  const layersRef = useRef<KeyboardLayersRef>({ layers: [initialLayer], currentShortcuts: {} });

  const keyHandler = useCallback((event: KeyboardEvent) => {
    const shortcuts = layersRef.current.currentShortcuts[event.key];
    if (!shortcuts || !shortcuts.size) return;

    let eventHandled = false;

    shortcuts.forEach((shortcut) => {
      // Convert the shortcut to a consistent format
      const normalizedShortcut: ShortcutConfig =
        typeof shortcut === 'function'
          ? {
              callback: shortcut,
              modifiers: { ctrl: false, alt: false, shift: false, meta: false },
            }
          : {
              ...shortcut,
              modifiers: shortcut.modifiers || {
                ctrl: false,
                alt: false,
                shift: false,
                meta: false,
              },
            };

      // Check if modifiers exactly match
      const modifiersMatch =
        event.ctrlKey === !!normalizedShortcut.modifiers.ctrl &&
        event.altKey === !!normalizedShortcut.modifiers.alt &&
        event.shiftKey === !!normalizedShortcut.modifiers.shift &&
        event.metaKey === !!normalizedShortcut.modifiers.meta;

      if (modifiersMatch) {
        event.preventDefault();
        normalizedShortcut.callback(event);
        eventHandled = true;
      }
    });

    // If no shortcut handled the event and it had modifiers, allow it to propagate
    if (!eventHandled && (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey)) {
      return;
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [keyHandler]);

  const addLayer = (layer: KeyboardLayer) => {
    const existingLayers = layersRef.current.layers;
    const foundLayer = existingLayers.some((l) => l.name === layer.name);

    if (!foundLayer || layer.mergeDuplicates) {
      const layers = [...existingLayers, layer];
      sortLayers(layers);
      const currentShortcuts = calculateCurrentShortcuts(layers);

      layersRef.current = { layers, currentShortcuts };
    }
  };

  const removeLayer = (name: string) => {
    const existingLayers = layersRef.current.layers;
    const foundLayer = existingLayers.some((l) => l.name === name);

    if (foundLayer) {
      const layers = existingLayers.filter((l) => l.name !== name);
      const currentShortcuts = calculateCurrentShortcuts(layers);

      layersRef.current = { layers, currentShortcuts };
    }
  };

  return (
    <KeyboardLayerContext.Provider value={{ addLayer, removeLayer }}>
      {children}
    </KeyboardLayerContext.Provider>
  );
};

const useKeyboardShortcuts = (layer: KeyboardLayer) => {
  const { addLayer, removeLayer } = useContext(KeyboardLayerContext);

  useEffect(() => {
    if (!addLayer || !layer) {
      return () => {};
    }

    addLayer(layer);
    return () => removeLayer(layer.name);
  }, [addLayer, layer, removeLayer]);
};

// Utility functions for creating shortcuts
export const createShortcut = (
  callback: KeyboardShortcut,
  modifiers?: ModifierKeys
): ShortcutConfig => ({
  callback,
  modifiers,
});

// Utility function for creating command shortcuts with modifiers
export const createCommandShortcut = (
  callback: KeyboardShortcut,
  additionalModifiers?: Omit<ModifierKeys, 'meta' | 'ctrl'>
): ShortcutConfig => ({
  callback,
  modifiers: {
    ...(typeof window !== 'undefined' && navigator.platform.toLowerCase().includes('mac')
      ? { meta: true }
      : { ctrl: true }),
    ...additionalModifiers,
  },
});

export default useKeyboardShortcuts;
